"use client";

import { motion } from "framer-motion";
import {
  TrendingUp,
  Users,
  DollarSign,
  Sparkles,
  ShoppingBag,
  Percent,
  Globe,
  Zap,
} from "lucide-react";
import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/section";

const keyBenefits = [
  {
    value: "25",
    suffix: "%+",
    label: "Min. Discount",
    description: "Every product guaranteed at least 25% off retail",
    icon: Percent,
  },
  {
    value: "0",
    suffix: "%",
    label: "Commission Fees",
    description: "Keep 100% of your profit — no marketplace cuts",
    icon: DollarSign,
  },
  {
    value: "150",
    suffix: "K+",
    label: "Products Listed",
    description: "Join a thriving marketplace of quality brands",
    icon: ShoppingBag,
  },
];

const whyMerchants = [
  {
    icon: Users,
    title: "New Customer Acquisition",
    description:
      "Reach customers actively searching for deals who may never find your standalone store.",
  },
  {
    icon: TrendingUp,
    title: "Incremental Revenue",
    description:
      "Additional sales channel that doesn't cannibalize your existing traffic.",
  },
  {
    icon: Sparkles,
    title: "AI-Powered Discounts",
    description:
      "Our AI negotiates personalized deals, helping convert browsers into buyers.",
  },
  {
    icon: Zap,
    title: "Zero Extra Work",
    description:
      "Orders sync automatically to Shopify. Fulfill like any other order.",
  },
];

export function WhyListSection() {
  return (
    <Section background="gray">
      <Container>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-display-1 text-ink mb-4">
            Why Merchants Love the Marketplace
          </h2>
          <p className="text-lg text-muted max-w-2xl mx-auto">
            An additional D2C sales channel that brings you new customers — with
            zero commissions and seamless Shopify integration.
          </p>
        </motion.div>

        {/* Key Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {keyBenefits.map((benefit, index) => (
            <motion.div
              // key={item.title}
              key={benefit.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center p-6 rounded-2xl bg-gradient-to-br from-brand-blue-soft to-panel border border-brand-blue-line"
            >
              <div className="inline-flex p-3 bg-brand-blue-soft rounded-xl mb-4">
                <benefit.icon className="w-6 h-6 text-brand-blue" />
              </div>
              <div className="text-4xl md:text-5xl font-bold text-brand-blue mb-1">
                {benefit.value}
                {benefit.suffix}
              </div>
              <div className="font-semibold text-ink mb-1">
                {benefit.label}
              </div>
              <p className="text-sm text-muted">{benefit.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Why Merchants Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {whyMerchants.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="bg-page rounded-xl p-5 border border-line hover:border-brand-blue-line hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="w-10 h-10 bg-brand-blue-soft rounded-lg flex items-center justify-center mb-3">
                  <item.icon className="w-5 h-5 text-brand-blue" />
                </div>
                <h3 className="font-semibold text-ink mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted">{item.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom highlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="rounded-2xl bg-gradient-to-br from-sky-500 to-sky-600 p-8 text-center text-white"
        >
          <div className="inline-flex p-3 bg-white/20 rounded-xl mb-4">
            <Globe className="w-8 h-8" />
          </div>
          <h3 className="text-2xl font-bold mb-3">
            Reach Customers You&apos;d Never Find Otherwise
          </h3>
          <p className="text-white/90 max-w-2xl mx-auto mb-4">
            Marketplace shoppers are deal-hunters actively looking for quality
            products at great prices. They discover brands through the Buddify
            app — not through ads or search engines.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            Included free with Brand Partnerships subscription
          </div>
        </motion.div>
      </Container>
    </Section>
  );
}
